import { X } from 'lucide-react';
import type { SearchResult } from './SearchFilterBar';
import { useNavigate } from 'react-router-dom';

interface SearchResultsListProps {
  results: SearchResult[];
  isSearching: boolean;
  searchQuery: string;
  onClearSearch: () => void;
}

export function SearchResultsList({ 
  results, 
  isSearching,
  searchQuery,
  onClearSearch 
}: SearchResultsListProps) {
  const navigate = useNavigate();

  if (!isSearching) {
    return null;
  }

  if (results.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <p className="text-gray-500 text-base sm:text-lg mb-4">
            No results found {searchQuery && `for "${searchQuery}"`}
          </p>
          <button
            onClick={onClearSearch}
            className="text-blue-600 hover:text-blue-700 underline text-sm sm:text-base"
          >
            Clear search
          </button>
        </div>
      </div>
    );
  }

  const handleViewExperts = (categoryName: string, subCategoryName: string) => {
    const formattedCat = categoryName.toLowerCase();
    const encodedSub = encodeURIComponent(subCategoryName);
    
    // Check if it's one of your dedicated routes (Astrology, Doctor, Lawyer)
    if (['astrology', 'doctor', 'lawyer'].includes(formattedCat)) {
      navigate(`/experts/${formattedCat}?subCategory=${encodedSub}`);
    } else {
      // Fallback for general categories
      navigate(`/experts?category=${encodeURIComponent(categoryName)}&subCategory=${encodedSub}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
          Search Results ({results.length})
        </h2>
        <button
          onClick={onClearSearch}
          className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-gray-600 hover:text-gray-900"
        >
          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Clear</span>
        </button>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {results.map((result, index) => (
          <div
            key={`${result.category.id}-${result.subCategory.id}-${index}`}
            className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              {/* Category Icon */}
              <div
                className={`w-12 h-12 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${result.category.bg || 'from-gray-100 to-gray-200'}`}
              >
                {result.category.icon ? (
                  <img 
                    src={result.category.icon} 
                    alt={result.category.name}
                    className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                  />
                ) : (
                  <span className="text-xl sm:text-2xl">📋</span>
                )}
              </div>
              
              <div className="flex-1 min-w-0 w-full">
                {/* Parent Category Badge */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded whitespace-nowrap">
                    {result.category.name}
                  </span>
                  {result.subCategory.isPopular && (
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full whitespace-nowrap">
                      Popular
                    </span>
                  )}
                  {result.subCategory.isTrending && (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full whitespace-nowrap">
                      Trending
                    </span>
                  )}
                </div>

                {/* Subcategory Name */}
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 break-words">
                  {result.subCategory.name}
                </h3>
                
                {/* Subcategory Description */}
                <p className="text-sm sm:text-base text-gray-600 mb-3 break-words">
                  {result.subCategory.description}
                </p>
              </div>

              {/* View Experts Button */}
              <button 
                onClick={() => handleViewExperts(result.category.name, result.subCategory.name)}
                className="w-full sm:w-auto px-4 sm:px-6 py-2 text-sm sm:text-base bg-brand-blue-900 text-white rounded-lg hover:bg-brand-blue-800 transition-colors whitespace-nowrap"
              >
                View Experts
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}